/**
 * @engine/input — Invert Processor
 *
 * Inverts axis/vector values.
 */

import type { ProcessorType, ControlValue } from '../types';
import { InputProcessor, registerProcessor } from './InputProcessor';

interface InvertParams {
  invertX?: boolean;
  invertY?: boolean;
  invertZ?: boolean;
}

/**
 * Invert processor — negates values
 */
export class InvertProcessor extends InputProcessor {
  readonly type: ProcessorType = 'invert';

  private readonly invertX: boolean;
  private readonly invertY: boolean;
  private readonly invertZ: boolean;

  constructor(params: Partial<InvertParams> = {}) {
    super(params as Record<string, unknown>);
    // Default: invert all
    this.invertX = params.invertX ?? true;
    this.invertY = params.invertY ?? true;
    this.invertZ = params.invertZ ?? true;
  }

  process(value: ControlValue): ControlValue {
    if (typeof value === 'number') {
      return this.invertX ? -value : value;
    }

    if (typeof value === 'boolean') {
      return this.invertX ? !value : value;
    }

    if (this.isVec2(value)) {
      return {
        x: this.invertX ? -value.x : value.x,
        y: this.invertY ? -value.y : value.y,
      };
    }

    if (this.isVec3(value)) {
      return {
        x: this.invertX ? -value.x : value.x,
        y: this.invertY ? -value.y : value.y,
        z: this.invertZ ? -value.z : value.z,
      };
    }

    return value;
  }
}

registerProcessor('invert', InvertProcessor);
